import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { auth } from '../firebase';
import { 
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  updateProfile
} from 'firebase/auth';
import axios from 'axios';
import TopBar from './TopBar';
import './Auth.css';

function Auth() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: '',
    coursesTutoring: []
  });
  const [isLogin, setIsLogin] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsPopup, setShowTermsPopup] = useState(false);

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      const user = userCredential.user;

      await updateProfile(user, { displayName: formData.username });

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/create-profile`, {
        uid: user.uid,
        email: formData.email,
        username: formData.username,
        coursesTutoring: formData.coursesTutoring
      });

      alert('Account created successfully!');
      setIsLogin(true);
      setFormData({ ...formData, password: '' });
    } catch (err) {
      setError(err.response?.data?.error || (err.message || '').replace('Firebase: ', ''));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await signInWithEmailAndPassword(auth, formData.email, formData.password);
    } catch (err) {
      setError(err.message.replace('Firebase: ', ''));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/google-signin`, {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const TermsAndConditionsPopup = () => (
    <AnimatePresence>
      {showTermsPopup && (
        <motion.div 
          className="terms-popup-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowTermsPopup(false)}
        >
          <motion.div 
            className="terms-popup-content"
            initial={{ opacity: 0, scale: 0.9, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 50 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="terms-header">
              <h2>Terms and Conditions</h2>
              <button 
                className="close-button"
                onClick={() => setShowTermsPopup(false)}
              >
                ×
              </button>
            </div>

            <div className="terms-body">
              <div className="terms-section">
                <h3>1. Acceptance of Terms</h3>
                <p>By accessing and using TeachMeGo, you accept and agree to be bound by the terms and provision of this agreement.</p>
              </div>

              <div className="terms-section">
                <h3>2. User Responsibilities</h3>
                <p>You are responsible for maintaining the confidentiality of your account and password. You agree to accept responsibility for all activities that occur under your account.</p>
              </div>

              <div className="terms-section">
                <h3>3. Tutoring Services</h3>
                <p>TeachMeGo provides a platform to connect students with tutors. We are not responsible for the quality of tutoring services or the outcomes of tutoring sessions.</p>
              </div>

              <div className="terms-section">
                <h3>4. Payment and Fees</h3>
                <p>All payments for tutoring services are processed through our secure payment system. Fees are non-refundable except as required by law.</p>
              </div>

              <div className="terms-section">
                <h3>5. Code of Conduct</h3>
                <p>Users must maintain professional conduct during all interactions. Any form of harassment, discrimination, or inappropriate behavior will result in immediate account termination.</p>
              </div>

              <div className="terms-section">
                <h3>6. Privacy Policy</h3>
                <p>Your personal information is protected under our Privacy Policy. We do not share your data with third parties without your consent.</p>
              </div>

              <div className="terms-section">
                <h3>7. Cancellation Policy</h3>
                <p>Sessions must be cancelled at least 24 hours in advance. Late cancellations may result in charges.</p>
              </div>

              <div className="terms-section">
                <h3>8. Modifications</h3>
                <p>TeachMeGo reserves the right to modify these terms at any time. Users will be notified of significant changes.</p>
              </div>
            </div>

            <div className="terms-footer">
              <motion.button
                className="agree-button"
                onClick={() => {
                  setAgreedToTerms(true);
                  setShowTermsPopup(false);
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                I Agree to Terms
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="auth-page">
      <TopBar />
      <div className="background-overlay"></div>

      {/* Terms and Conditions Popup */}
      <TermsAndConditionsPopup />

      {/* Animated Auth Card */}
      <motion.div 
        className="auth-card"
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      >
        <motion.div 
          className="auth-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <motion.h2 
            initial={{ opacity: 0, y: -20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.4 }}
          >
            {isLogin ? 'Welcome Back 👋' : 'Join Us ✨'}
          </motion.h2>

          {error && (
            <motion.div 
              className="error-message"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {error}
            </motion.div>
          )}

          <motion.form
            onSubmit={isLogin ? handleLogin : handleSignUp}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            {!isLogin && (
              <motion.input
                type="text"
                placeholder="Username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                minLength="3"
                whileFocus={{ scale: 1.02 }}
              />
            )}

            <motion.input
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              whileFocus={{ scale: 1.02 }}
            />

            <motion.input
              type="password"
              placeholder="Password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              minLength="6"
              whileFocus={{ scale: 1.02 }}
            />

            {!isLogin && (
              <motion.div 
                className="terms-checkbox"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <input
                  type="checkbox"
                  id="agreeTerms"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                />
                <label htmlFor="agreeTerms">
                  I agree to the{' '}
                  <button 
                    type="button"
                    className="terms-link"
                    onClick={() => setShowTermsPopup(true)}
                  >
                    Terms and Conditions
                  </button>
                </label>
              </motion.div>
            )}

            <motion.button
              type="submit"
              disabled={loading || (!isLogin && !agreedToTerms)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {loading ? 'Processing...' : isLogin ? 'Login' : 'Sign Up'}
            </motion.button>
          </motion.form>

          <motion.div 
            className="auth-options"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <motion.button 
              onClick={handleGoogleSignIn}
              className="google-link"
              disabled={loading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Sign in with Google
            </motion.button>
            
            <motion.button 
              onClick={() => {
                setIsLogin(!isLogin);
                setFormData({ ...formData, password: '' });
                setAgreedToTerms(false);
              }}
              className="toggle-link"
              disabled={loading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isLogin ? 'Need an account? Sign Up' : 'Have an account? Login'}
            </motion.button>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default Auth;
